import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Progress,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
} from "antd";
import {
  ArrowRightOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileDoneOutlined,
  PlusOutlined,
  ThunderboltOutlined,
  UsergroupAddOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
dayjs.extend(utc);
dayjs.extend(timezone);
import { query } from "./api";
import { TaskNameSelect } from "./task-name";
import { reportTaskId, updateReportSearch } from "./report-filters";
import { businessLabels, taskPath, type TaskBusiness } from "./business";
import { type Agent, integer } from "./domain";
import { RefreshButton, Resource, SectionTitle, useResource } from "./shared";
export interface Report {
  as_of: string;
  timezone: string;
  from: string;
  to: string;
  market_id: string;
  currency: string;
  coverage: {
    event_recording_started_at: string | null;
    note: string;
    historical_attempts_complete: boolean;
  };
  overview: {
    claimed_people: number;
    claimed_shares: number;
    submitted_shares: number;
    submission_attempts: number;
    approved_shares: number;
    pending_review_shares: number;
  };
  progress: {
    task_id: number;
    title: string;
    target_shares: number;
    claimed_shares: number;
    approved_shares: number;
    completion_rate: number | null;
  }[];
  daily_trends: {
    date: string;
    claimed_shares: number;
    submitted_attempts: number;
    approved_attempts: number;
    rejected_attempts: number;
    reward_minor: number;
  }[];
  cohort_funnel: {
    claimed_shares: number;
    submitted_shares: number;
    approved_shares: number;
    rewarded_shares: number;
    submit_rate: number | null;
    approval_rate: number | null;
  };
  quality: {
    first_reviewed_shares: number;
    first_approved_shares: number;
    first_approval_rate: number | null;
    final_reviewed_shares: number;
    final_approved_shares: number;
    final_approval_rate: number | null;
  };
  review_latency: {
    sample_attempts: number;
    median_seconds: number | null;
    p90_seconds: number | null;
  };
  rewards: { credited_shares: number; member_task_reward_minor: number };
  exceptions: {
    overdue_shares: number;
    rejected_shares: number;
    cancelled_shares: number;
    near_deadline_shares: number;
  };
}
const rate = (value: number | null | undefined) =>
  value == null ? "—" : `${(value * 100).toFixed(1)}%`;
const duration = (seconds: number | null) =>
  seconds == null
    ? "—"
    : seconds < 60
      ? `${seconds} 秒`
      : seconds < 3600
        ? `${(seconds / 60).toFixed(1)} 分钟`
        : `${(seconds / 3600).toFixed(1)} 小时`;
const progressColumns = (business?: TaskBusiness) => [
  {
    title: "任务名称",
    dataIndex: "title",
    render: (v: string, r: Report["progress"][number]) => (
      <Link to={business ? taskPath(business, r.task_id) : `/tasks/${r.task_id}`}>{v}</Link>
    ),
  },
  { title: "目标份数", dataIndex: "target_shares", align: "right" as const },
  { title: "已领取", dataIndex: "claimed_shares", align: "right" as const },
  { title: "已通过", dataIndex: "approved_shares", align: "right" as const },
  {
    title: "完成率",
    dataIndex: "completion_rate",
    render: (v: number | null) => (
      <div style={{ minWidth: 130 }}>
        {v == null ? (
          "—"
        ) : (
          <Progress
            percent={Math.round(v * 100)}
            size="small"
            strokeColor="#437ae7"
          />
        )}
      </div>
    ),
  },
];
function Metric({
  label,
  value,
  icon,
  tone = "blue",
}: {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  tone?: string;
}) {
  return (
    <Card className="metric-card">
      <div className="metric-top">
        <span>{label}</span>
        <span className={`metric-icon ${tone}`}>{icon}</span>
      </div>
      <div className="metric-value">{value}</div>
    </Card>
  );
}
function TrendChart({ rows }: { rows: Report["daily_trends"] }) {
  const [series, setSeries] = useState<
    "claimed_shares" | "submitted_attempts" | "approved_attempts"
  >("claimed_shares");
  const max = Math.max(4, Math.ceil(Math.max(0, ...rows.map((r) => r[series])) / 4) * 4);
  const w = 760,
    h = 220,
    p = 34;
  const points = rows.map((r, i) => [
    rows.length === 1 ? w / 2 : p + (i * (w - 2 * p)) / (rows.length - 1),
    h - p - (r[series] / max) * (h - 2 * p),
  ]);
  return (
    <>
      <div className="chart-toolbar">
        <Select
          size="small"
          value={series}
          onChange={setSeries}
          options={[
            { value: "claimed_shares", label: "领取份数" },
            { value: "submitted_attempts", label: "提交次数" },
            { value: "approved_attempts", label: "通过次数" },
          ]}
        />
      </div>
      {!rows.length ? (
        <Empty description="此时间范围内暂无趋势数据" />
      ) : (
        <div className="trend-wrap">
          <svg
            viewBox={`0 0 ${w} ${h}`}
            role="img"
            aria-label="每日任务执行趋势"
          >
            <defs>
              <linearGradient id="trend-fill" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#3979ef" stopOpacity=".23" />
                <stop offset="100%" stopColor="#3979ef" stopOpacity="0" />
              </linearGradient>
            </defs>
            {[0, 0.25, 0.5, 0.75, 1].map((n) => (
              <g key={n}>
                <line
                  x1={p}
                  x2={w - p}
                  y1={h - p - n * (h - 2 * p)}
                  y2={h - p - n * (h - 2 * p)}
                  stroke="#e9eef5"
                  strokeDasharray="4 4"
                />
                <text
                  x="0"
                  y={h - p - n * (h - 2 * p) + 4}
                  fill="#93a0b2"
                  fontSize="11"
                >
                  {Math.round(max * n)}
                </text>
              </g>
            ))}
            {rows.length > 1 && <path
              d={`M${points[0].join(",")} ${points.map(([x, y]) => `L${x},${y}`).join(" ")} L${w - p},${h - p} L${p},${h - p} Z`}
              fill="url(#trend-fill)"
            />}
            <polyline
              points={points.map((v) => v.join(",")).join(" ")}
              fill="none"
              stroke="#3979ef"
              strokeWidth="3"
              strokeLinejoin="round"
            />
            {points.map(([x, y], i) => (
              <circle
                key={i}
                cx={x}
                cy={y}
                r={rows.length > 35 ? 2 : 3}
                fill="#fff"
                stroke="#3979ef"
                strokeWidth="2"
              >
                <title>{`${rows[i].date}：${rows[i][series]}`}</title>
              </circle>
            ))}
            {rows
              .filter(
                (_, i) =>
                  i === 0 ||
                  i === rows.length - 1 ||
                  i % Math.max(1, Math.floor(rows.length / 5)) === 0,
              )
              .map((r) => (
                <text
                  key={r.date}
                  x={
                    rows.length === 1 ? w / 2 : p +
                    (rows.indexOf(r) * (w - 2 * p)) /
                      Math.max(1, rows.length - 1)
                  }
                  y={h - 4}
                  textAnchor="middle"
                  fill="#93a0b2"
                  fontSize="11"
                >
                  {r.date.slice(5)}
                </text>
              ))}
          </svg>
        </div>
      )}
    </>
  );
}
function OverviewMetrics({ r }: { r: Report }) {
  return (
    <Row gutter={[20, 20]}>
      <Col xs={24} sm={12} xl={6}>
        <Metric
          label="领取人数"
          value={integer(r.overview.claimed_people)}
          icon={<UsergroupAddOutlined />}
        />
      </Col>
      <Col xs={24} sm={12} xl={6}>
        <Metric
          label="领取份数"
          value={integer(r.overview.claimed_shares)}
          icon={<FileDoneOutlined />}
          tone="violet"
        />
      </Col>
      <Col xs={24} sm={12} xl={6}>
        <Metric
          label="审核通过份数"
          value={integer(r.overview.approved_shares)}
          icon={<CheckCircleOutlined />}
          tone="green"
        />
      </Col>
      <Col xs={24} sm={12} xl={6}>
        <Metric
          label="待审核份数"
          value={integer(r.overview.pending_review_shares)}
          icon={<ClockCircleOutlined />}
          tone="amber"
        />
      </Col>
    </Row>
  );
}
export function Overview({ agent }: { agent: Agent }) {
  const resource = useResource<Report>("/reports");
  return (
    <>
      <SectionTitle
        eyebrow="WORKSPACE OVERVIEW"
        title={`${greeting()}，${agent.name}`}
        description="关注任务进展，让每一份体验都有清晰的反馈。"
        action={
          <Link to="/tasks/new">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={agent.business_frozen}
            >
              新建任务
            </Button>
          </Link>
        }
      />
      <div className="welcome-banner">
        <div>
          <Tag color="blue" bordered={false}>
            协作 · 洞察 · 成长
          </Tag>
          <h2>你的业务全貌，一目了然。</h2>
          <p>从发布任务到结果反馈，专注每一次高质量体验。</p>
          <Link to="/tasks">
            管理我的任务 <ArrowRightOutlined />
          </Link>
        </div>
        <div className="welcome-graphic" aria-hidden>
          <div className="graphic-orbit" />
          <div className="graphic-card">
            <BarVisual />
            <span>让数据成为方向</span>
          </div>
          <div className="graphic-bubble">
            <CheckCircleOutlined />
          </div>
        </div>
      </div>
      <Resource resource={resource}>
        {(r) => (
          <>
            <OverviewMetrics r={r} />
            <Row gutter={[20, 20]} style={{ marginTop: 24 }}>
              <Col xs={24} xl={16}>
                <Card
                  title="任务执行趋势"
                  extra={
                    <Link to="/reports?tab=daily">
                      查看详细报表 <ArrowRightOutlined />
                    </Link>
                  }
                >
                  <TrendChart rows={r.daily_trends} />
                </Card>
              </Col>
              <Col xs={24} xl={8}>
                <Card
                  title="领取转化漏斗"
                  className="funnel-card"
                >
                  <Funnel r={r} />
                </Card>
              </Col>
              <Col span={24}>
                <Card
                  title="任务进度"
                  extra={
                    <Link to="/reports?tab=progress">
                      全部任务 <ArrowRightOutlined />
                    </Link>
                  }
                >
                  <Table
                    size="middle"
                    rowKey="task_id"
                    columns={progressColumns()}
                    dataSource={r.progress.slice(0, 5)}
                    pagination={false}
                    locale={{
                      emptyText: <Empty description="还没有任务执行数据" />,
                    }}
                    scroll={{ x: 660 }}
                  />
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Resource>
    </>
  );
}
function greeting() {
  const hour = Number(
    new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      hour12: false,
      timeZone: "Asia/Shanghai",
    }).format(new Date()),
  );
  return hour < 12 ? "上午好" : hour < 18 ? "下午好" : "晚上好";
}
function BarVisual() {
  return (
    <div className="bar-visual">
      {[40, 66, 48, 83, 70, 96].map((h, i) => (
        <i key={i} style={{ height: h }} />
      ))}
    </div>
  );
}
function Funnel({ r }: { r: Report }) {
  const f = r.cohort_funnel;
  const rows = [
    ["已领取", f.claimed_shares],
    ["已提交", f.submitted_shares],
    ["已通过", f.approved_shares],
  ] as const;
  return (
    <div className="funnel">
      {rows.map(([name, value], i) => (
        <div className="funnel-row" key={name}>
          <div>
            <span>
              <i
                style={{
                  background: ["#3575e6", "#6192ef", "#80b2f7", "#9fd4e6"][i],
                }}
              />
              {name}
            </span>
            <strong>{integer(value)}</strong>
          </div>
          <div className="funnel-bar">
            <i
              style={{
                width: f.claimed_shares
                  ? `${Math.max(1, (value / f.claimed_shares) * 100)}%`
                  : "0%",
                background: ["#3575e6", "#6192ef", "#80b2f7", "#9fd4e6"][i],
              }}
            />
          </div>
        </div>
      ))}
      <div className="funnel-summary">
        <span>
          提交转化率 <b>{rate(f.submit_rate)}</b>
        </span>
        <span>
          审核通过率 <b>{rate(f.approval_rate)}</b>
        </span>
      </div>
    </div>
  );
}
const reportTabs = [
  ["overview", "经营总览"],
  ["progress", "任务进度"],
  ["daily", "每日趋势"],
  ["cohort", "领取批次漏斗"],
  ["quality", "完成质量"],
  ["efficiency", "审核效率"],
  ["exceptions", "异常任务"],
];
export function Reports({ business }: { business: TaskBusiness }) {
  return <BusinessReports key={business} business={business} />;
}
function BusinessReports({ business }: { business: TaskBusiness }) {
  const { message } = App.useApp();
  const [params, setParams] = useSearchParams();
  const [range, setRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(29, "day"),
    dayjs(),
  ]);
  const taskId = reportTaskId(params);
  const tab = params.get("tab") || "overview";
  const resource = useResource<Report>(
    `/reports${query({ business, from: range[0].startOf("day").tz("Asia/Shanghai", true).toISOString(), to: range[1].format("YYYY-MM-DD") === dayjs().tz("Asia/Shanghai").format("YYYY-MM-DD") ? undefined : range[1].endOf("day").tz("Asia/Shanghai", true).toISOString(), timezone: "Asia/Shanghai", task_id: taskId })}`,
  );
  return (
    <>
      <SectionTitle
        eyebrow="ONLINE ANALYTICS"
        title={`${businessLabels[business]}报表`}
        description={`仅统计${businessLabels[business]}任务的执行与质量。`}
        action={<RefreshButton onClick={resource.reload} />}
      />
      <Card className="filter-card">
        <Space wrap size={16}>
          <span className="filter-label">时间范围</span>
          <DatePicker.RangePicker
            value={range}
            allowClear={false}
            maxDate={dayjs()}
            onChange={(v) => {
              if (v?.[0] && v[1] && v[1].diff(v[0], "day") < 93)
                setRange([v[0], v[1]]);
              else if (v?.[0] && v[1])
                message.warning("最多查询 93 天，请缩短时间范围");
            }}
            disabledDate={(current) => current.isAfter(dayjs(), "day")}
            presets={[
              {
                label: "近 7 天",
                value: [dayjs().subtract(6, "day"), dayjs()],
              },
              {
                label: "近 30 天",
                value: [dayjs().subtract(29, "day"), dayjs()],
              },
              {
                label: "近 90 天",
                value: [dayjs().subtract(89, "day"), dayjs()],
              },
            ]}
          />
          <TaskNameSelect
            business={business}
            placeholder="全部任务，可按名称搜索"
            value={taskId}
            onChange={(value?: number) => setParams((current) => updateReportSearch(current, "task_id", value))}
          />
        </Space>
      </Card>
      <Tabs
        activeKey={tab}
        onChange={(v) => setParams((current) => updateReportSearch(current, "tab", v))}
        items={reportTabs.map(([key, label]) => ({ key, label }))}
      />
      <Resource resource={resource}>
        {(r) => (
          <>
            {tab === "overview" && (
              <>
                <OverviewMetrics r={r} />
                <Card title="任务执行趋势" style={{ marginTop: 24 }}>
                  <TrendChart rows={r.daily_trends} />
                </Card>
              </>
            )}
            {tab === "progress" && (
              <Card title="任务完成进度">
                <Table
                  rowKey="task_id"
                  columns={progressColumns(business)}
                  dataSource={r.progress}
                  pagination={{ pageSize: 10, showSizeChanger: true }}
                  scroll={{ x: 660 }}
                />
              </Card>
            )}
            {tab === "daily" && (
              <>
                <Card title="每日任务趋势">
                  <TrendChart rows={r.daily_trends} />
                </Card>
                <Card title="按日明细" style={{ marginTop: 24 }}>
                  <Table
                    rowKey="date"
                    dataSource={r.daily_trends}
                    columns={[
                      { title: "日期", dataIndex: "date" },
                      { title: "领取份数", dataIndex: "claimed_shares" },
                      { title: "提交次数", dataIndex: "submitted_attempts" },
                      { title: "通过次数", dataIndex: "approved_attempts" },
                      { title: "驳回次数", dataIndex: "rejected_attempts" },
                    ]}
                    pagination={{ pageSize: 15 }}
                    scroll={{ x: 700 }}
                  />
                </Card>
              </>
            )}
            {tab === "cohort" && (
              <Row gutter={[24, 24]}>
                <Col span={24}>
                  <Card title="同批次领取转化">
                    <Funnel r={r} />
                  </Card>
                </Col>
              </Row>
            )}
            {tab === "quality" && (
              <Row gutter={[24, 24]}>
                <Col xs={24} lg={12}>
                  <Card title="首次审核质量">
                    <Statistic
                      title="首次审核通过率"
                      value={rate(r.quality.first_approval_rate)}
                    />
                    <QualityProgress value={r.quality.first_approval_rate} />
                    <div className="stat-pair">
                      <span>
                        首次审核 {integer(r.quality.first_reviewed_shares)} 份
                      </span>
                      <span>
                        首次通过 {integer(r.quality.first_approved_shares)} 份
                      </span>
                    </div>
                  </Card>
                </Col>
                <Col xs={24} lg={12}>
                  <Card title="最终审核结果">
                    <Statistic
                      title="最终审核通过率"
                      value={rate(r.quality.final_approval_rate)}
                    />
                    <QualityProgress value={r.quality.final_approval_rate} />
                    <div className="stat-pair">
                      <span>
                        最终审核 {integer(r.quality.final_reviewed_shares)} 份
                      </span>
                      <span>
                        最终通过 {integer(r.quality.final_approved_shares)} 份
                      </span>
                    </div>
                  </Card>
                </Col>
              </Row>
            )}
            {tab === "efficiency" && (
              <Row gutter={[20, 20]}>
                <Col xs={24} md={8}>
                  <Metric
                    label="审核样本"
                    value={integer(r.review_latency.sample_attempts)}
                    icon={<FileDoneOutlined />}
                  />
                </Col>
                <Col xs={24} md={8}>
                  <Metric
                    label="审核时长中位数"
                    value={duration(r.review_latency.median_seconds)}
                    icon={<ClockCircleOutlined />}
                    tone="green"
                  />
                </Col>
                <Col xs={24} md={8}>
                  <Metric
                    label="P90 审核时长"
                    value={duration(r.review_latency.p90_seconds)}
                    icon={<ThunderboltOutlined />}
                    tone="amber"
                  />
                </Col>
              </Row>
            )}
            {tab === "exceptions" && (
              <Row gutter={[20, 20]}>
                {(
                  [
                    ["逾期任务", "overdue_shares"],
                    ["审核驳回", "rejected_shares"],
                    ["已取消", "cancelled_shares"],
                    ["临近截止", "near_deadline_shares"],
                  ] as const
                ).map(([label, key]) => (
                  <Col key={key} xs={24} sm={12} xl={6}>
                    <Metric
                      label={label}
                      value={integer(r.exceptions[key])}
                      icon={<ClockCircleOutlined />}
                      tone="amber"
                    />
                  </Col>
                ))}
              </Row>
            )}
          </>
        )}
      </Resource>
    </>
  );
}
function QualityProgress({ value }: { value: number | null }) {
  return value == null ? (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description="尚无有效审核样本"
    />
  ) : (
    <Progress
      percent={Number((value * 100).toFixed(1))}
      strokeColor="#16a085"
      style={{ margin: "20px 0" }}
    />
  );
}
